import type { ReactNode } from 'react';
import { useFieldContext, useFieldValue, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { VideoUpload } from './-components/video-upload';

/**
 * Form field for video uploads. The field value encodes three states:
 * - `undefined` = no change (keep existing server video)
 * - `File` = new file uploaded
 * - `''` = existing video removed
 */
export default function VideoUploadField(props: {
	currentUrl: string | undefined;
	buttonLabel: string;
	label?: string;
	description?: ReactNode;
	emptyText?: string;
	noVideoText?: string;
	removeVideoText?: string;
	videoHintText?: string;
	dropZoneDescription?: string;
	children?: ReactNode;
}) {
	const field = useFieldContext<File | '' | undefined>();
	const formReadOnly = useFormReadOnly();
	const isOptional = useIsFieldOptional(field.name);
	const value = useFieldValue(field);
	const isRemoved = value === '';

	return (
		<div>
			{props.label && (
				<div className="mb-2">
					<FieldLabelRow
						label={props.label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						info={props.description}
					/>
				</div>
			)}
			<VideoUpload
				id={field.name}
				currentUrl={isRemoved ? undefined : props.currentUrl}
				file={value instanceof File ? value : undefined}
				onChange={(file) => field.handleChange(file)}
				onRemove={() => field.handleChange('')}
				label={props.buttonLabel}
				readOnly={formReadOnly}
				emptyText={props.emptyText}
				noVideoText={props.noVideoText}
				removeVideoText={props.removeVideoText}
				videoHintText={props.videoHintText}
				dropZoneDescription={props.dropZoneDescription}
			>
				{props.children}
			</VideoUpload>
		</div>
	);
}
